import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface Collaborator {
  id: number;
  tenant_id: number;
  name: string;
  email: string | null;
  telegram_chat_id: string | null;
  vonigo_user_code: string | null;
  role: "owner" | "general_manager" | "sales_rep" | "dispatcher" | "technician" | "read_only" | "tech_admin";
  status: "active" | "paused" | "on_leave" | "inactive";
  is_active: boolean;
  created_at: string;
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

export function useCollaborators() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["settings", "collaborators"],
    queryFn: () => fetchJson<{ items: Collaborator[] }>("/api/cockpit/settings/collaborators", { headers }),
  });
}

export function useCollaboratorMutations() {
  const headers = useAuthHeaders(true);
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["settings", "collaborators"] });
  return {
    create: useMutation({
      mutationFn: (payload: Partial<Collaborator>) => fetchJson<Collaborator>("/api/cockpit/settings/collaborators", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: Partial<Collaborator> }) => fetchJson<Collaborator>(`/api/cockpit/settings/collaborators/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: number) => fetchJson<Collaborator>(`/api/cockpit/settings/collaborators/${id}`, {
        method: "DELETE",
        headers,
      }),
      onSuccess: invalidate,
    }),
  };
}
